<template>

  <ion-page id="lichessarena-page">
    <ion-content>
      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />
      </div>

      <div v-show="showState == SHOW_TOURNAMENTS" :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
        <TopLine :style="styleTopline" title="Lichess Arena Tournaments" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
        <ion-list id="lichess-arenas" :style="styleMiddle" >
          <ion-item v-show="isLoading">
            <ion-label>
              <h1>{{ loadingArenaString }}</h1>
            </ion-label>
          </ion-item>
          <ion-item>
            <ion-label color="danger" >
              {{ sayString }}
            </ion-label>
          </ion-item>
          <ion-item v-show="startedTournaments.length > 0">
            <ion-label >
              <h1 class="myheading" >{{ tournamentsNowString }}</h1>
            </ion-label>
          </ion-item>
          <ion-item v-for="item in startedTournaments" v-bind:key="item.index" @click="onClick('started',item.index)"
                  :class="{
                  'selected-text-dark' : item.selected && isDark,
                  'selected-text-light' : item.selected && !isDark
                  }">
            <ion-label>
              "{{ item.index+1 }}" : {{ item.name }} {{  item.duration }} 
              <span style="color:red;">{{ item.rated }} {{ timeString }}:{{ item.time}}</span> {{ playersString }}: {{ item.players }}
            </ion-label>
          </ion-item>

          <ion-item v-show="createdTournaments.length > 0">
            <ion-label >
              <h1 class="myheading" >{{ tournamentsSoonString }}</h1>
            </ion-label>
          </ion-item>
          <ion-item v-for="item in createdTournaments" v-bind:key="item.index" @click="onClick('created',item.index)"
                  :class="{
                  'selected-text-dark' : item.selected && isDark,
                  'selected-text-light' : item.selected && !isDark
                  }">
            <ion-label>
              "{{ item.index+1 + startedTournaments.length }}" : {{ item.name }} {{  item.duration }} 
              <span style="color:red;">{{ item.rated }} {{ timeString }}:{{ item.time}}</span> {{ playersString }}: {{ item.players }}
        
            </ion-label>
          </ion-item>
        </ion-list>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>
  
</template>

<script setup>

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';

  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight,  scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import { ref, watch } from 'vue'
  import { computed } from "@vue/reactivity"

  import ExplainTour from '@/components/ExplainTour.vue'
  import ToolBar from '@/components/ToolBar.vue'
  import TopLine from '@/components/TopLine.vue'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { useGetData , getLichessData, generateErrorAlertText } from '../../composables/getData.js'
  import { parseWordsSaid , buildWordListFromChoiceList, parseNumberWords } from '@/composables/wordlistSupport.js'
import { translateEnglishWords, translatePhrase } from '@/composables/translation.js';

  const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
    wordsSaid : {
      type : String,
      default : ""
    },
    wordsSaidTrigger : {
      type : Number,
      default : 0
    }
  })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()
  const windowSizeStore = useWindowSizeStore()

  const lichessAccessToken = connectionStore.getLichessAccessToken

  const isDark = ref(connectionStore.getDarkmode != "light")

  const wordlistArenas = "command scroll up down tournament join leave explain done ignore mute listen one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen twenty thirty forty fifty sixty seventy eighty ninety";
  const wordlist = ref(wordlistArenas)
  emit("word-list",wordlistArenas)

  const loadingArenaString = ref(translatePhrase("Loading Lichess Arena Tournaments"))
  const tournamentsSoonString = ref(translatePhrase("Tournaments Starting Soon"))
  const tournamentsNowString = ref(translatePhrase("Tournaments On Right Now"))
  const sayString = ref(translateEnglishWords("Say ' command Tournament # ' to Highlight"))
  const playersString = ref(translateEnglishWords("Players"))
  const timeString = ref(translateEnglishWords("Time"))
  
  const isLoading = ref(true)

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  const SHOW_TOURNAMENTS = 0
  const SHOW_EXPLAINTOUR = 1
  const showState = ref(SHOW_TOURNAMENTS)

  const TOOLBAR_JOIN = 0
  const TOOLBAR_LEAVE = 1
  const TOOLBAR_EXPLAIN = 2

  const toolbarElements = ref([
    { index : TOOLBAR_JOIN, label : "Join" ,disabled : true},
    { index : TOOLBAR_LEAVE, label : "Leave" ,disabled : true},
    { index : TOOLBAR_EXPLAIN, label : "Explain" ,disabled : false},
  ])

  const toolbarTrigger = ref(0)

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  var scrollTop = 0

  const startedTournaments = ref([])
  const createdTournaments = ref([])

  var selectedTournament = null
  var selectedAction = ""

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    getArenaTournaments()
  }

  watch(() => windowSizeStore.getWindowSize, () => {
    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())
  })
  
  watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
  })

  function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  // START Explain Tour Routines

  function explainTourInitialize(html) {
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (html == null) ? "lichess_arena" : html;
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlist.value)
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {
    case "mute" :
      microphoneIsMuted.value = true 
      emit("mic-is-muted",microphoneIsMuted.value)
      connectionStore.setMicrophoneIsMuted(true)
      break
    case "listen" :
      microphoneIsMuted.value = false 
      emit("mic-is-muted",microphoneIsMuted.value)
      connectionStore.setMicrophoneIsMuted(false)
      break
    case "done" :
      showState.value = SHOW_TOURNAMENTS
      wordlist.value = wordlistArenas
      emit("word-list",wordlist.value)
      break
    }
  }

  // END Explain Tour Routines


  function toolbarClick(arrayIndex) {
    switch (arrayIndex) {
      case TOOLBAR_JOIN :
        processWords("command join")
        break
      case TOOLBAR_LEAVE :
        processWords("command leave")
        break
      case TOOLBAR_EXPLAIN :
        processWords("command explain")
        break
    }
  }

  function onClick(which,index) {

    // remove any selected
    for (var i=0; i < startedTournaments.value.length; i++)
      startedTournaments.value[i].selected = false
    for (var i=0; i < createdTournaments.value.length; i++)
      createdTournaments.value[i].selected = false
      
    if (which == "started") {
      startedTournaments.value[index].selected = true
      selectedTournament = startedTournaments.value[index]
    }
    else {
      createdTournaments.value[index].selected = true
      selectedTournament = createdTournaments.value[index]
    }
  
    toolbarElements.value[TOOLBAR_JOIN].disabled = false
    toolbarElements.value[TOOLBAR_LEAVE].disabled = false
    toolbarTrigger.value++
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain") {
            return ["explain",""]
        }
        if (currentWord == "done") {
            return ["done",""]
        }
        if (currentWord == "listen") {
            return ["listen",""]
        }
        if (currentWord == "mute") {
            return ["mute",""]
        }
        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
        if (currentWord == "tournament") {
          const tournamentIndex = parseNumberWords(nextWord,nextNextWord)
          return ["tournament", tournamentIndex]
        }
        if (currentWord == "join")
          return ["join",""]
        if (currentWord == "leave")
          return ["leave",""]
       
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

  function processWords(text) {
    //console.log("text in engine Menu ",text)

    if (showState.value == SHOW_EXPLAINTOUR) {
      explainTourWordsSpoken.value = text
      explainTourWordsSpokenTrigger.value++
      return;
    }

    var returnArray = parseWordsSaid(text,parseCommands)
    //console.log("command found",returnArray)
    switch (returnArray[0]) {
      case "scroll" :
        //console.log("scroll element",returnArray[1])
        scrollElement("lichess-arenas",returnArray[1])
        break
      case "tournament" :
        const tournamentIndex = returnArray[1] - 1
        if (tournamentIndex < 0)
          break
        if (tournamentIndex < startedTournaments.value.length) {
          onClick("started",tournamentIndex)
          break
        }
        if (tournamentIndex < startedTournaments.value.length + createdTournaments.value.length) {
          onClick("created",tournamentIndex - startedTournaments.value.length)
          break
        }
        break
      case "join" :
        if (selectedTournament == null)
          break
        selectedAction = "join"
        joinLeaveTournament("join",selectedTournament)
        break
      case "leave" :
        if (selectedTournament == null)
          break
        selectedAction = "leave"
        joinLeaveTournament("withdraw",selectedTournament)
        break
      case "explain" :
        explainTourInitialize(null)
        break
     case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        emit("where-next","/lichessmenu")
        //router.replace({ path: "/lichessmenu"})
        break
    }
  }
  
  function processJsonArenaTournament(tournament, timenow, loop) {
        
    const id = tournament["id"]
    const variantElement = tournament["variant"]
    const perfElement = tournament["perf"]
    const clockElement = tournament["clock"]

    // id, rated, number of players and title
    const fullName = tournament["fullName"]
    const playerCount = tournament["nbPlayers"]
    const ratedNumber = tournament["rated"]
    const startsAt = tournament["startsAt"]
    const finishesAt = tournament["finishesAt"]

    if (id == undefined || variantElement == undefined || perfElement == undefined ||
        clockElement == undefined || fullName == undefined || playerCount == undefined ||
        ratedNumber == undefined || startsAt == undefined || finishesAt == undefined)
        return
    
    // variant key=standard
    const variantKey = variantElement["key"]
    if (variantKey == undefined || variantKey != "standard")
        return
    
    // perf key=rapid
    const perfKey = perfElement["key"]
    if (perfKey == undefined || perfKey != "rapid")
        return

    // time & increment
    const clockLimit = clockElement["limit"]
    const clockIncrement = clockElement["increment"]
    if (clockLimit == undefined || clockIncrement == undefined)
        return
  
    const clockLimitMinutes = Math.floor(clockLimit / 60)
    const startsAtSeconds = Math.floor(startsAt / 1000)
    const finishAtSeconds = Math.floor(finishesAt / 1000)

    var timeDurationString = ""
    var startTimeString = ""

    // tournament is on now
    if (startsAtSeconds <= timenow) {
        const timeLeftInMinutes = Math.floor((finishAtSeconds - timenow) / 60)
        timeDurationString = translateEnglishWords("Now for") + " " + timeLeftInMinutes + " " + translateEnglishWords("minutes")
        startTimeString = "Now"
    } 
    else {
        const timeLeftInMinutes = Math.floor((finishAtSeconds - startsAtSeconds) / 60)
        
        const secondsUntilStart = Math.floor(startsAtSeconds - timenow)
        const minutesUntilStart = Math.floor(secondsUntilStart / 60)
        const startMinutes = Math.floor(minutesUntilStart % 60)
        const startHours = Math.floor(minutesUntilStart / 60)
        
        startTimeString = (startMinutes < 10) ? startHours + ":0" + startMinutes : 
                                                    startHours + ":" +startMinutes
        timeDurationString = translateEnglishWords("In") + " " + startTimeString + " " + translateEnglishWords("for") + " " + timeLeftInMinutes + " " + translateEnglishWords("mins")
    }

    const ratedString = (ratedNumber == 1) ? translateEnglishWords("Rated") : translateEnglishWords("Casual")
    const clockTimeString = clockLimitMinutes + "+" + clockIncrement

    const index = (loop == 0) ? startedTournaments.value.length : createdTournaments.value.length
    const oneTournament = { index : index,
            id : id,
            hasStarted : (loop == 0),
            name : translateEnglishWords(fullName),
            duration : timeDurationString,
            startTime : startTimeString,
            rated : ratedString,
            time : clockTimeString,
            players : playerCount,
            selected : false
          }

    //console.log("ONE ",oneTournament)
    if (loop == 0)
      startedTournaments.value.push(oneTournament)
    else
      createdTournaments.value.push(oneTournament)
  }

  function processJsonResponse(json) {
    //console.log(" arena tournament callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize("ERROR:" + generateErrorAlertText(json))
      return;
    }

    const timenow = Math.floor(Date.now() / 1000)
    for (var loop = 0; loop < 2; loop++) {
      const loop_string = (loop == 0) ? "started" : "created";

      if (json.hasOwnProperty(loop_string) == false)
        continue
      const tournamentArray = json[loop_string]
      for (var i=0; i < tournamentArray.length; i++) {
        const t = tournamentArray[i]
        //console.log("tournament",loop,i,t)
        processJsonArenaTournament(t, timenow, loop)
      }
    }
    isLoading.value = false
  }

  function getArenaTournaments() {
    const urlStr = "https://lichess.org/api/tournament"
    getLichessData(urlStr, lichessAccessToken,null, processJsonResponse) 
  }

  function processJoinLeaveJsonResponse(json) {
    //console.log(" arena tournament join leave callback",json)

    // joining tournament. They might not qualify (not enough games, rating too high, ...)
    // also, tournament might have already started
           
    //console.log("tournament",selectedTournament)
    const errorString = json.hasOwnProperty('error') ? json["error"] : ""
    const fullName = selectedTournament.name
    const startTimeString = selectedTournament.startTime
    var title = ""
    var message = ""
        
    const isJoin = (selectedAction == "join")
    if (isJoin) {
      if (errorString != "") {
        title = "Join Error"
        message = "Attempt to join<p>" + fullName + "<p>" + "Returned Error ->\n" + errorString
      }
      else
      if (selectedTournament.hasStarted) {
        title = "Arena Tournament Joined"
        message = fullName + "<p>" + "Has already started." + "<p>" +
                        "Go to the 'Open Games List' on the 'Lichess Menu' now to see your pairing.<p>" +
                        "Pairings sometimes a little while to show up so use the 'Refresh' button on that screen every so often"
      }
      else {
        title = "Arena Tournament Joined"
        message = fullName + "<p>" + "Will start in -> " + startTimeString + "<p>" +
                                "At that time, go to the 'Open Games List' on the 'Lichess Menu to get your pairing.<p>" +
                                "Pairings sometimes a little while to show up so use the 'Refresh' button on that screen every so often"
      }
    }
    else {
      title = "Leave Sent"
      message = "You have now left " + fullName + "<p>" +
                    "Please note it's possible you had not joined that tournament. No indication is available to this app"
    }
    
    const html = "HTML:" + "<h3>" + title + "</h3>" + "<p>" + message
    explainTourInitialize(html)
  }

  function joinLeaveTournament(operation,tournament) {

    const id = tournament.id
    var formData = { }
    const urlStr = "https://lichess.org/api/tournament/" + id + "/" + operation

    getLichessData(urlStr, lichessAccessToken,formData, processJoinLeaveJsonResponse) 
  }

</script>

<style scoped>

.myheading {
  background: #4c6ca0;
  color : white;
  font-size : 20px;
}

.selected-text-light {
  color : white;
  background : yellow;
  padding: 2px;
  border : solid 1px black;
  border-radius: 3px;
}

.selected-text-dark {
  color : #fff;
  padding: 2px;
  border : solid 1px #fff;
  border-radius: 3px;
}

</style>
